/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export type EvType = 'small' | 'medium' | 'large';

// Public 50/50 city/highway averages, kWh per 100 km from socket.
// Sources: ANWB, EV-Database, RVO public ranges. Conservative round numbers.
export const EV_CONSUMPTION_KWH_PER_100KM: Record<EvType, number> = {
  small: 16,
  medium: 18,
  large: 23,
};

export const MAX_EVS = 5;
export const DEFAULT_KM_PER_YEAR = 15000;
export const MAX_KM_PER_YEAR = 200000;
export const VOLUME_MIN = 0;
export const VOLUME_MAX = 20000;
export const VOLUME_STEP = 500;
// Upper safety cap for manual entry (fleet/business). Slider stays at VOLUME_MAX.
export const VOLUME_MANUAL_MAX = 10_000_000;

export interface EvRow {
  type: EvType;
  km: number;
}

export function computeRoundedKwh(evs: EvRow[]): number {
  const total = evs.reduce(
    (sum, ev) => sum + (ev.km * EV_CONSUMPTION_KWH_PER_100KM[ev.type]) / 100,
    0
  );
  return Math.min(
    VOLUME_MANUAL_MAX,
    Math.max(VOLUME_MIN, Math.round(total / VOLUME_STEP) * VOLUME_STEP)
  );
}

// ── Zakelijk (fleet) estimator ───────────────────────────────────────────────
// Business audiences think in fleets, not single cars: enter a COUNT of vehicles
// per class with an average yearly mileage, instead of one card per vehicle.

export type FleetClass = 'car' | 'van' | 'truck';

export const FLEET_CLASSES: FleetClass[] = ['car', 'van', 'truck'];

// Public consumption averages, kWh per 100 km from socket. Conservative round
// numbers; heavy transport (truck) varies strongly with payload/route — the
// modal disclaimer flags this. Sources: EV-Database, RVO, manufacturer ranges.
export const FLEET_CONSUMPTION_KWH_PER_100KM: Record<FleetClass, number> = {
  car: 19,
  van: 30,
  truck: 120,
};

// Business vehicles typically drive more than private ones.
export const DEFAULT_FLEET_KM_PER_YEAR: Record<FleetClass, number> = {
  car: 25000,
  van: 30000,
  truck: 60000,
};

export const MAX_FLEET_COUNT = 999;
export const MAX_CHARGERS = 9999;

export interface FleetRow {
  class: FleetClass;
  count: number;
  kmPerYear: number;
}

export function makeDefaultFleet(): FleetRow[] {
  return FLEET_CLASSES.map((c) => ({
    class: c,
    count: c === 'car' ? 1 : 0,
    kmPerYear: DEFAULT_FLEET_KM_PER_YEAR[c],
  }));
}

export function computeFleetKwh(rows: FleetRow[]): number {
  const total = rows.reduce(
    (sum, r) => sum + (r.count * r.kmPerYear * FLEET_CONSUMPTION_KWH_PER_100KM[r.class]) / 100,
    0
  );
  return Math.min(
    VOLUME_MANUAL_MAX,
    Math.max(VOLUME_MIN, Math.round(total / VOLUME_STEP) * VOLUME_STEP)
  );
}
